import styled from 'styled-components';
import { Container } from '@/components/common/Container';
import { Section } from '@/components/common/Section';
import { theme } from '@/styles/theme';

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;

  h1 {
    margin-bottom: ${theme.spacing.xl};
  }

  h2 {
    margin-top: ${theme.spacing.xxl};
    margin-bottom: ${theme.spacing.lg};
    color: ${theme.colors.primary.main};
  }

  h3 {
    margin-top: ${theme.spacing.xl};
    margin-bottom: ${theme.spacing.md};
  }

  p, li {
    color: ${theme.colors.text.darkGray};
    line-height: 1.8;
  }

  ul {
    list-style: disc;
    margin-left: ${theme.spacing.xl};
    margin-bottom: ${theme.spacing.lg};
  }

  li {
    margin-bottom: ${theme.spacing.sm};
  }

  strong {
    color: ${theme.colors.text.black};
    font-weight: ${theme.fontWeights.semibold};
  }

  .last-updated {
    font-size: ${theme.fontSizes.sm};
    color: ${theme.colors.text.gray};
    margin-bottom: ${theme.spacing.xxl};
  }

  .contact-email {
    color: ${theme.colors.primary.main};
    text-decoration: underline;
  }
`;

const PrivacyPolicy = () => {
  return (
    <Section $padding="large">
      <Container>
        <Content>
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: {new Date().toLocaleDateString()}</p>

          <p>
            At Lexifox, we respect your privacy and are committed to protecting your personal data.
            This privacy policy explains how we collect, use, and safeguard your information when
            you use our mobile application and website.
          </p>

          <h2>1. Information We Collect</h2>

          <h3>1.1 Information You Provide</h3>
          <p>When you use Lexifox, we may collect the following information:</p>
          <ul>
            <li><strong>Account Information:</strong> Email address, username, and password when you create an account</li>
            <li><strong>Profile Information:</strong> Learning preferences, language selections, and proficiency level</li>
            <li><strong>Learning Data:</strong> Your vocabulary progress, words learned, practice history, and performance statistics</li>
            <li><strong>Communication Data:</strong> Messages you send to our support team</li>
          </ul>

          <h3>1.2 Information Collected Automatically</h3>
          <ul>
            <li><strong>Usage Data:</strong> App features used, time spent learning, session frequency</li>
            <li><strong>Device Information:</strong> Device type, operating system, app version, device identifiers</li>
            <li><strong>Analytics Data:</strong> App crashes, performance metrics, and usage patterns</li>
          </ul>

          <h2>2. How We Use Your Information</h2>

          <p>We use your information to:</p>
          <ul>
            <li><strong>Provide and improve our service:</strong> Deliver personalized vocabulary learning experiences</li>
            <li><strong>Spaced repetition algorithm:</strong> Optimize when you see words based on your learning patterns</li>
            <li><strong>Track progress:</strong> Show you statistics on words learned, streaks, and achievements</li>
            <li><strong>Sync across devices:</strong> Keep your learning progress consistent across all your devices</li>
            <li><strong>Send notifications:</strong> Remind you to practice (only if you opt in)</li>
            <li><strong>Customer support:</strong> Respond to your questions and resolve issues</li>
            <li><strong>Improve our app:</strong> Analyze usage patterns to enhance features and user experience</li>
            <li><strong>Security:</strong> Detect and prevent fraud, abuse, and technical issues</li>
          </ul>

          <h2>3. Data Storage and Security</h2>

          <h3>3.1 Where We Store Your Data</h3>
          <p>
            Your learning data is stored securely on cloud servers. {/* TODO: Specify actual server locations */}
            We use industry-standard encryption to protect your data both in transit and at rest.
          </p>

          <h3>3.2 How Long We Keep Your Data</h3>
          <ul>
            <li><strong>Active accounts:</strong> We retain your data for as long as your account is active</li>
            <li><strong>Deleted accounts:</strong> Data is deleted within 90 days of account deletion</li>
            <li><strong>Backups:</strong> Backup copies may persist for up to 90 additional days</li>
          </ul>

          <h3>3.3 Security Measures</h3>
          <p>We implement appropriate technical and organizational measures to protect your data, including:</p>
          <ul>
            <li>Encryption of data in transit using TLS/SSL</li>
            <li>Encryption of data at rest</li>
            <li>Regular security assessments and updates</li>
            <li>Access controls and authentication requirements</li>
            <li>Monitoring for unauthorized access</li>
          </ul>

          <h2>4. Data Sharing and Disclosure</h2>

          <p>
            <strong>We do not sell your personal information.</strong> We may share your data only in the
            following limited circumstances:
          </p>

          <h3>4.1 Service Providers</h3>
          <p>
            We work with trusted third-party service providers who help us operate our app (e.g., cloud hosting,
            analytics, payment processing). These providers only access data necessary to perform their functions
            and are contractually obligated to protect it.
          </p>

          <h3>4.2 Legal Requirements</h3>
          <p>We may disclose your information if required to do so by law or in response to:</p>
          <ul>
            <li>Valid legal process (subpoenas, court orders)</li>
            <li>Protecting the rights, property, or safety of Lexifox, our users, or the public</li>
            <li>Enforcing our Terms of Service</li>
          </ul>

          <h3>4.3 Business Transfers</h3>
          <p>
            If Lexifox is involved in a merger, acquisition, or sale of assets, your information may be
            transferred. We will notify you before your data is transferred and becomes subject to a different
            privacy policy.
          </p>

          <h2>5. Your Privacy Rights</h2>

          <p>You have the following rights regarding your personal data:</p>

          <ul>
            <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
            <li><strong>Data portability:</strong> Receive your data in a portable format</li>
            <li><strong>Opt-out:</strong> Unsubscribe from marketing emails and push notifications</li>
            <li><strong>Withdraw consent:</strong> Where processing is based on consent, you can withdraw it at any time</li>
          </ul>

          <p>
            To exercise these rights, contact us at{' '}
            <a href="mailto:privacy@lexifox.com" className="contact-email">
              privacy@lexifox.com
            </a>
            {/* TODO: Update email address */}
          </p>

          <h2>6. Children's Privacy</h2>

          <p>
            Lexifox is designed for users aged 13 and older. We do not knowingly collect personal information
            from children under 13. If you believe we have collected information from a child under 13, please
            contact us immediately at{' '}
            <a href="mailto:privacy@lexifox.com" className="contact-email">
              privacy@lexifox.com
            </a>
          </p>

          <h2>7. Offline Usage</h2>

          <p>
            Lexifox allows you to practice offline. When offline, your learning data is stored locally on your
            device and synced to our servers when you reconnect to the internet.
          </p>

          <h2>8. Third-Party Services</h2>

          <h3>8.1 Analytics</h3>
          <p>
            We use analytics services to understand how users interact with our app. These services may collect
            usage data using cookies and similar technologies.
          </p>

          <h3>8.2 Payment Processing</h3>
          <p>
            If you purchase premium features, payment information is processed by third-party payment providers
            (Apple App Store, Google Play Store). We do not store your credit card information.
          </p>

          <h2>9. International Data Transfers</h2>

          <p>
            If you access Lexifox from outside {/* TODO: Add your country/region */}, your information may be
            transferred to and processed in countries where our servers are located. We ensure appropriate
            safeguards are in place to protect your data in accordance with this privacy policy.
          </p>

          <h2>10. Cookies and Tracking Technologies</h2>

          <p>
            Our website uses cookies and similar technologies to improve your experience. You can control
            cookie preferences through your browser settings. Note that disabling cookies may limit certain
            features of our website.
          </p>

          <h2>11. Updates to This Policy</h2>

          <p>
            We may update this privacy policy from time to time. We will notify you of significant changes by:
          </p>
          <ul>
            <li>Posting the new policy on this page</li>
            <li>Updating the "Last Updated" date</li>
            <li>Sending an email notification (for material changes)</li>
          </ul>

          <p>Your continued use of Lexifox after changes indicates your acceptance of the updated policy.</p>

          <h2>12. Contact Us</h2>

          <p>If you have questions or concerns about this privacy policy or our data practices, please contact us:</p>

          <p>
            <strong>Email:</strong>{' '}
            <a href="mailto:privacy@lexifox.com" className="contact-email">
              privacy@lexifox.com
            </a>
            <br />
            {/* TODO: Add physical address if required by local regulations */}
            {/* <strong>Address:</strong> [Company Address] */}
          </p>

    
        </Content>
      </Container>
    </Section>
  );
};

export default PrivacyPolicy;
